import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Plus, 
  Minus, 
  ShoppingCart, 
  CalendarIcon, 
  Users, 
  MapPin,
  Sparkles,
  ChefHat,
  Wine,
  Music,
  Truck,
  X,
  ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  food: <ChefHat className="h-5 w-5" />,
  drinks: <Wine className="h-5 w-5" />,
  entertainment: <Music className="h-5 w-5" />,
  staffing: <Users className="h-5 w-5" />,
  logistics: <Truck className="h-5 w-5" />
};

const CATEGORY_COLORS = {
  food: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  drinks: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  entertainment: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  staffing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  logistics: 'bg-green-500/20 text-green-400 border-green-500/30'
};

export default function PricingCalculator() {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [catalog, setCatalog] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [eventDetails, setEventDetails] = useState({
    title: '',
    event_type: 'corporate',
    event_date: null,
    guest_count: 50,
    venue: ''
  });
  const [activeCategory, setActiveCategory] = useState('food');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async () => {
    try {
      const response = await axios.get(`${API}/services/catalog`);
      setCatalog(response.data);
    } catch (error) {
      console.error('Failed to fetch catalog:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const addItem = (item) => {
    const existing = selectedItems.find(i => i.item_id === item.item_id);
    if (existing) {
      setSelectedItems(selectedItems.map(i => 
        i.item_id === item.item_id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setSelectedItems([...selectedItems, { ...item, quantity: 1 }]);
    }
    toast.success(`Added ${item.name}`);
  };

  const removeItem = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.item_id !== itemId));
  };

  const updateQuantity = (itemId, delta) => {
    setSelectedItems(selectedItems.map(i => {
      if (i.item_id === itemId) {
        const newQty = Math.max(1, i.quantity + delta);
        return { ...i, quantity: newQty };
      }
      return i;
    }));
  };

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateTotal() * 0.08;
  };

  const handleSaveEvent = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save your event');
      navigate('/login');
      return;
    }

    if (!eventDetails.title || !eventDetails.event_date) {
      toast.error('Please fill in event details');
      return;
    }

    if (selectedItems.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    try {
      const response = await axios.post(`${API}/events`, {
        ...eventDetails,
        event_date: eventDetails.event_date.toISOString(),
        selected_items: selectedItems
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Event saved successfully!');
      navigate(`/events/${response.data.event_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const groupedItems = selectedItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pt-16">
        <div className="animate-pulse text-muted-foreground">Loading services...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-2">
            <Sparkles className="h-5 w-5 text-indigo-500" />
            <span className="text-indigo-400 text-sm font-medium">Dynamic Pricing</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2">
            Event Calculator
          </h1>
          <p className="text-muted-foreground">
            Build your perfect event. Add services and see costs update instantly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Service Catalog - 3 columns */}
          <div className="lg:col-span-3 space-y-6">
            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2">
              {Object.keys(catalog).map(category => (
                <Button
                  key={category}
                  variant={activeCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveCategory(category)}
                  className={activeCategory === category 
                    ? 'bg-indigo-500 hover:bg-indigo-600' 
                    : 'border-border text-muted-foreground hover:bg-muted'}
                  data-testid={`category-tab-${category}`}
                >
                  {CATEGORY_ICONS[category]}
                  <span className="ml-2 capitalize">{category}</span>
                </Button>
              ))}
            </div>

            {/* Service Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {catalog[activeCategory]?.map(item => (
                <Card 
                  key={item.item_id} 
                  className="bg-card border-border hover:border-indigo-500/50 transition-all overflow-hidden group"
                  data-testid={`service-item-${item.item_id}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                    <Badge className={`absolute top-3 left-3 ${CATEGORY_COLORS[item.category]}`}>
                      {item.category}
                    </Badge>
                    <div className="absolute bottom-3 right-3 bg-card/90 backdrop-blur-sm rounded-lg px-3 py-1">
                      <span className="text-lg font-bold text-foreground">${item.price.toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground">/person</span>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-heading font-semibold text-foreground mb-1">{item.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                    <Button 
                      size="sm" 
                      className="w-full bg-indigo-500/20 hover:bg-indigo-500 text-indigo-400 hover:text-foreground border border-indigo-500/30"
                      onClick={() => addItem(item)}
                      data-testid={`add-item-${item.item_id}`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Event
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Quote Summary - 2 columns */}
          <div className="lg:col-span-2">
            <div className="sticky top-24 space-y-6">
              {/* Event Details Card */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-heading text-foreground">Event Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Event Name</Label>
                    <Input
                      placeholder="My Event"
                      value={eventDetails.title}
                      onChange={(e) => setEventDetails({...eventDetails, title: e.target.value})}
                      className="bg-muted border-border text-foreground"
                      data-testid="event-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Event Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left bg-muted border-border text-foreground"
                          data-testid="event-date-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {eventDetails.event_date 
                            ? format(eventDetails.event_date, 'PPP')
                            : 'Select date'
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-muted border-border">
                        <Calendar
                          mode="single"
                          selected={eventDetails.event_date}
                          onSelect={(date) => setEventDetails({...eventDetails, event_date: date})}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Guests</Label>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <Input
                          type="number"
                          min="1"
                          value={eventDetails.guest_count}
                          onChange={(e) => setEventDetails({...eventDetails, guest_count: parseInt(e.target.value) || 1})}
                          className="bg-muted border-border text-foreground"
                          data-testid="guest-count-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Venue</Label>
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="TBD"
                          value={eventDetails.venue}
                          onChange={(e) => setEventDetails({...eventDetails, venue: e.target.value})}
                          className="bg-muted border-border text-foreground"
                          data-testid="venue-input"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Live Receipt */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-heading text-foreground flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-2 text-indigo-500" />
                    Your Quote
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedItems.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Add services to build your quote
                    </p>
                  ) : (
                    <>
                      {/* Grouped Items */}
                      {Object.entries(groupedItems).map(([category, items]) => (
                        <div key={category} className="space-y-2">
                          <div className="flex items-center space-x-2">
                            {CATEGORY_ICONS[category]}
                            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                              {category}
                            </span>
                          </div>
                          {items.map(item => (
                            <div 
                              key={item.item_id}
                              className="flex items-center space-x-3 bg-muted/50 rounded-lg p-3"
                              data-testid={`quote-item-${item.item_id}`}
                            >
                              <img 
                                src={item.image_url} 
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                                <p className="text-xs text-muted-foreground">${item.price.toFixed(2)} × {item.quantity}</p>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                  onClick={() => updateQuantity(item.item_id, -1)}
                                >
                                  <Minus className="h-3 w-3" />
                                </Button>
                                <span className="w-6 text-center text-foreground text-sm">{item.quantity}</span>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                                  onClick={() => updateQuantity(item.item_id, 1)}
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-6 w-6 text-red-400 hover:text-red-300"
                                  onClick={() => removeItem(item.item_id)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}

                      <Separator className="bg-muted" />

                      {/* Totals */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="text-foreground">${calculateTotal().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Tax (8%)</span>
                          <span className="text-foreground">${calculateTax().toFixed(2)}</span>
                        </div>
                        <Separator className="bg-muted" />
                        <div className="flex justify-between text-lg font-bold">
                          <span className="text-foreground">Total</span>
                          <span className="text-lime-400">${(calculateTotal() + calculateTax()).toFixed(2)}</span>
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-indigo-500 hover:bg-indigo-600 text-foreground mt-4"
                        onClick={handleSaveEvent}
                        data-testid="save-event-btn"
                      >
                        {isAuthenticated ? 'Save & Continue' : 'Sign In to Save'}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
